import React, { Suspense } from 'react';
import {
    MemoryRouter,
    Navigate,
    Route,
    Routes,
    UNSAFE_LocationContext,
    UNSAFE_RouteContext,
} from 'react-router-dom';
import './InlineDaqiShell.scss';
import { AppLoader } from '../../../../../routing/AppLoader';
import { paths } from '../../../../../routing/path';
import { DataRequestDashboardPage } from '../../../../DataRequest/DataRequestDashboardPage/DataRequestDashboardPage';
import { ProjectEditorHost } from '../../../../Admin/DataRequest/ProjectEditorHost/ProjectEditorHost';

export interface InlineDaqiShellProps {
    initialPath: string;
}

// Spike (N5-591): renders the DAQI dashboard/editor pages inside a
// MemoryRouter instead of the same-origin iframe. The MemoryRouter gives
// DAQI's own useNavigate/useSearchParams/useLocation calls a private
// history stack, so the marketplace page's real browser URL never changes.
// Both the "-built-in" paths (what the iframe embed used) and their plain
// counterparts are registered because some DAQI navigation is driven by the
// global isBuiltInMode redux flag, which this shell intentionally leaves
// untouched (see DEIFRAME-NOTES.md) — so a navigate() call can land on
// either variant depending on that flag's value.
// react-router v6 refuses to mount a <Router> inside another router's
// context ("You cannot render a <Router> inside another <Router>"), and the
// marketplace page lives under the app's BrowserRouter. Nulling the two
// UNSAFE_* contexts detaches this subtree from the outer router so the
// MemoryRouter mounts as if it were the root — the documented escape hatch
// for embedded routers until v6 supports nesting officially.
const detachedRouteContext: React.ContextType<typeof UNSAFE_RouteContext> = {
    outlet: null,
    matches: [],
    isDataRoute: false,
};

export const InlineDaqiShell = (props: InlineDaqiShellProps) => (
    <div className="inline-daqi-shell">
        <UNSAFE_LocationContext.Provider value={null as any}>
            <UNSAFE_RouteContext.Provider value={detachedRouteContext}>
                <MemoryRouter initialEntries={[props.initialPath]}>
                    <Suspense fallback={<AppLoader />}>
                        <Routes>
                            <Route
                                path={paths.builtIn.dataRequestDashboard}
                                element={<DataRequestDashboardPage isBuiltInMode={true} />}
                            />
                            <Route
                                path={paths.public.dataRequestDashboard}
                                element={<DataRequestDashboardPage isBuiltInMode={true} />}
                            />
                            <Route
                                path={paths.builtIn.projectEditor}
                                element={<ProjectEditorHost isBuiltInMode={true} />}
                            />
                            <Route
                                path={paths.admin.projectEditor}
                                element={<ProjectEditorHost isBuiltInMode={true} />}
                            />
                            {/* Any DAQI navigation to a route not registered
                                above would otherwise render a blank pane in the
                                in-memory history. Snap back to the initial entry
                                (dashboard + its query params) so a missed route
                                degrades visibly rather than silently. */}
                            <Route
                                path="*"
                                element={
                                    <Navigate to={props.initialPath} replace />
                                }
                            />
                        </Routes>
                    </Suspense>
                </MemoryRouter>
            </UNSAFE_RouteContext.Provider>
        </UNSAFE_LocationContext.Provider>
    </div>
);
